import React, { useState } from 'react'
import { ChevronDown, ChevronUp, LucideIcon } from 'lucide-react'

interface ContentPage {
  id: string
  name: string
  slug: string
  icon?: string
  requiresAuth?: boolean
}

interface HorizontalNavigationProps {
  pages: ContentPage[]
  activePageSlug: string
  onPageChange: (slug: string) => void
  tabAlignment: 'left' | 'center' | 'right'
  primaryColor?: string
}

// Icon mapping based on page icon field
const getPageIcon = (iconName?: string): LucideIcon | null => {
  if (!iconName) return null
  
  // Dynamic icon import would be handled by parent component
  // This is a placeholder - actual implementation would use a icon registry
  return null
}

export const HorizontalNavigation: React.FC<HorizontalNavigationProps> = ({
  pages,
  activePageSlug,
  onPageChange,
  tabAlignment = 'center',
  primaryColor = '#1D31FF'
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  
  const activePage = pages.find(page => page.slug === activePageSlug)
  
  const getAlignmentClass = () => {
    switch (tabAlignment) {
      case 'left':
        return 'justify-start'
      case 'right':
        return 'justify-end'
      case 'center':
      default:
        return 'justify-center'
    }
  }
  
  const handlePageClick = (page: ContentPage) => {
    if (!page.requiresAuth) {
      onPageChange(page.slug)
      setMobileMenuOpen(false)
    }
  }
  
  return (
    <div className="sticky top-0 z-10 bg-white shadow-md">
      <div className="container mx-auto px-4">
        {/* Desktop Navigation */}
        <div className="hidden md:block overflow-x-auto">
          <div className={`flex ${getAlignmentClass()} min-w-fit`}>
            {pages.map((page) => {
              const Icon = page.icon ? getPageIcon(page.icon) : null
              const isActive = page.slug === activePageSlug
              const isDisabled = page.requiresAuth
              
              return (
                <button
                  key={page.id}
                  onClick={() => handlePageClick(page)}
                  disabled={isDisabled}
                  className={`
                    flex flex-col items-center py-4 px-6 border-b-2 transition-colors
                    ${isActive 
                      ? '' 
                      : isDisabled
                        ? 'border-transparent text-gray-400 cursor-not-allowed'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }
                  `}
                  style={isActive ? { borderColor: primaryColor, color: primaryColor } : {}}
                >
                  {Icon && <Icon className="h-5 w-5 mb-2" />}
                  <span className="font-medium text-sm whitespace-nowrap">
                    {page.name}
                  </span>
                  {isDisabled && (
                    <span className="text-xs text-gray-400 mt-1">
                      (Login Required)
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
        
        {/* Mobile Navigation */}
        <div className="md:hidden">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex items-center justify-between w-full py-4 px-4 text-gray-700"
          >
            <div className="flex items-center gap-2">
              {activePage?.icon && (() => {
                const ActiveIcon = getPageIcon(activePage.icon)
                return ActiveIcon ? <ActiveIcon className="h-5 w-5" /> : null
              })()}
              <span className="font-medium">
                {activePage?.name || 'Select Page'}
              </span>
            </div>
            {mobileMenuOpen ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </button>
          
          {mobileMenuOpen && (
            <div className="bg-white border-t border-gray-100 py-2">
              {pages.map((page) => {
                const Icon = page.icon ? getPageIcon(page.icon) : null
                const isActive = page.slug === activePageSlug
                const isDisabled = page.requiresAuth
                
                return (
                  <button
                    key={page.id}
                    onClick={() => handlePageClick(page)}
                    disabled={isDisabled}
                    className={`
                      flex items-center gap-3 w-full px-4 py-3
                      ${isActive 
                        ? '' 
                        : isDisabled
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-gray-50'
                      }
                    `}
                    style={isActive ? { 
                      backgroundColor: `${primaryColor}10`, // 10% opacity
                      color: primaryColor 
                    } : {}}
                  >
                    {Icon && <Icon className="h-5 w-5" />}
                    <span className="font-medium text-sm">{page.name}</span>
                    {isDisabled && (
                      <span className="text-xs text-gray-400 ml-auto">
                        (Login Required)
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}